import PageErrorFallback from '@app/components/layout/page/components/page-error-fallback';
import { ErrorBoundary } from '@sentry/react';
import { useGetGroupAccount } from '@shared/api/hooks/group';
import { AccountFeatures, ChildOrganizationSummary } from '@shared/api/types';
import { createContext, useContext } from 'react';
import { Outlet } from 'react-router-dom';

type Status = 'error' | 'success' | 'loading' | 'pending';

export type GroupContextType = {
  /** Feature flags for the parent org — drives sidebar locking. */
  groupFeatures: AccountFeatures | null;
  /** Flat list of child orgs used by the global org filter. */
  organizations: ChildOrganizationSummary[];
  status: Status;
};

const Context = createContext<GroupContextType | null>(null);

export function useGroupContext() {
  return useContext(Context) as GroupContextType;
}

export default function GroupContextProvider() {
  const groupAccount = useGetGroupAccount();

  // Render-blocking status: we need group features before deciding what nav to
  // show, so use the account query's status as the gate.
  const context: GroupContextType = {
    groupFeatures: groupAccount.data?.groupFeatures ?? null,
    organizations: groupAccount.data
      ? [...groupAccount.data.organizations]
      : [],
    status: groupAccount.status,
  };

  return (
    <ErrorBoundary fallback={<PageErrorFallback />}>
      <Context.Provider value={context}>
        <Outlet />
      </Context.Provider>
    </ErrorBoundary>
  );
}
